import { getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';

import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';

// Shared by the calendar bar and the full-page assignment view so both can
// flash/dim just-assigned POs the same way without a store-level timer.
// `now` ticks once, right when the active window expires, so the highlight
// classes fall away without polling — the store itself never schedules anything.
export function useRecentAssignmentHighlight() {
  const store = useCapacityCalendarStore();
  const now = ref(Date.now());
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  watch(
    () => store.recentAssignments,
    (recent) => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      if (!recent) {
        return;
      }
      now.value = Date.now();
      const delay = Math.max(recent.until - Date.now(), 0);
      timeoutId = setTimeout(() => {
        now.value = Date.now();
      }, delay);
    },
    { immediate: true },
  );

  // Lifecycle hook only binds when called inside a component's setup();
  // the unit tests call the composable bare.
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    });
  }

  function isActiveForWeek(weekKey: string | undefined | null): boolean {
    const recent = store.recentAssignments;
    if (!recent || !weekKey || !recent.weekKeys.includes(weekKey)) {
      return false;
    }
    return now.value < recent.until;
  }

  function matchingPoNumbers(weekKey: string | undefined | null): ReadonlySet<string> {
    if (!isActiveForWeek(weekKey)) {
      return new Set();
    }
    return new Set(store.recentAssignments?.poNumbers ?? []);
  }

  return { isActiveForWeek, matchingPoNumbers };
}
